import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, CardMedia, Modal, ToggleButtonGroup, ToggleButton, Typography, Slider, Link, Alert } from '@mui/material';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';

import * as api from '../../api/index.js';

import { getCroppedImg } from '../utils/images/getCroppedImg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faImage, faCrop, faTrash } from '@fortawesome/free-solid-svg-icons'
import DsasButton from './DsasButton/DsasButton';
import { getUser } from '../../actions/auth.js';


const RATIO_PRESETS = [
  { value: 1, label: '1:1' },          // carré
  { value: 4 / 3, label: '4:3' },      // paysage léger
  { value: 16 / 9, label: '16:9' },    // paysage wide
  { value: 3 / 4, label: '3:4' },      // portrait
  { value: 9 / 16, label: '9:16' },    // story / mobile
  { value: 4 / 5, label: '4:5' },      // portrait Instagram
];

const ImageHandlerTool = ({ initialImage = null, onImageSave, isEditable=true, type=null, displayImageLabel=true, uploadButtonLabel='Charger une photo' }) => {
    const [errorApiCall, setErrorApiCall] = useState(false);  
    const [previewImage, setPreviewImage] = useState(initialImage);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); 
    const [showCropper, setShowCropper] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [aspect, setAspect] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    //Delai call API remove.bg
    const [lastClickTime, setLastClickTime] = useState(0);
    const [canCallAPI, setCanCallAPI] = useState(true);

    useEffect(() => {
      setPreviewImage(initialImage);
    }, [initialImage]); // Met à jour `previewImage` dès que `initialImage` change

    const handleReset = () => {
      setPreviewImage(initialImage);
      setUploadedImage(null);
      setProcessedImage(null);
      setLoading(false);
      setStep(1); 
      setShowCropper(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setAspect(1);
      setCroppedAreaPixels(null);
      //Delai call API remove.bg
      setLastClickTime(0);
      setCanCallAPI(true);
      setErrorApiCall(false);
      onImageSave('')
    }

    // PLAYER PHOTO EDITION
    // Etape 1 --> charger image par user. Affichage dans cadre preview1.
    // Etape 2 --> afficher boutons tools ('recadrer l'image' et 'supprimer l'arrière plan').
    // Etape 3 --> après supression background image, ou après redimentionnement image, afficher le résultat dans cadre de preview2
    // N'avoir que 2 cadres différents, 1 pour affichage preview image dans player data (preview1), et le 2e (preview2) ne s'affciche qu'après suppression de l'arrière plan ou redimensionnement pour que le user choisisse quelle version il garde.
    // Une fois version choisie on repart sur étape 1.

  // Handle image selection
  const handleSelectImage = async (e) => {
    
    const file = e.target.files[0];
  
    if (file) {
      try {
        const options = { maxSizeMB: 0.25, maxWidthOrHeight: 1024 };
        const compressedFile = await imageCompression(file, options);
        onImageSave(compressedFile)

        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImage(reader.result); // Afficher l'image compressée
          setUploadedImage(reader.result); // Stocker pour d'autres manipulations
        };
        reader.readAsDataURL(compressedFile);
        setStep(2); // Show tools for further editing
      } catch (error) {
        console.error('Image compression error:', error);
      }
    }
  };

  // Get boolean to know if user can call API again
  const getAPICallAllowed = () => {
    const currentTime = Date.now();
    const APICalldelay = process.env.REACT_APP_REMOVE_BG_CALL_DELAI_MS
    setCanCallAPI(currentTime - lastClickTime < (APICalldelay ? APICalldelay : 5000)); // default set to 5 sec
    return currentTime - lastClickTime < (APICalldelay ? APICalldelay : 5000); // milliseconds
  }

  
  const getApiKey = async () => {
  
      try{
          const currentUser = getUser()?.sub
          const serviceName = "remove_bg"
          const apiData = await api.getUserApiKey({userName : currentUser, serviceName : serviceName})
          if (!apiData?.data?.apiKey){
              return process.env.REACT_APP_REMOVE_BG_DEFAULT_API_KEY
          }else{
              return apiData.data.apiKey
          }

      } catch(error){
          console.log(error.message)
      } 
  }

  

  const handleRemoveBackground = async () => {

    setErrorApiCall(false);

    //get max allowed call time
    const APICalldelay = getAPICallAllowed();
    // Si le dernier clic est récent, empêcher l'appel
    if (APICalldelay) { 
      return;
    }

    setLoading(true);
    setLastClickTime(Date.now()); // Mettre à jour le dernier temps de clic
    
    try {

      const REMOVE_BG_API_KEY = await getApiKey();
      // console.log("Using API key:", REMOVE_BG_API_KEY);
      

      const formData = new FormData();
      // formData.append('size', 'auto');
      // Preview = sortie limitée à 0,25 mégapixel et facturée 0,25 crédit par image (environ 625 × 400 selon son ratio)
      formData.append('size', 'preview');
  
      if (uploadedImage) {
        // Vérifier si uploadedImage est déjà en base64, sinon convertir
        const isBase64 = uploadedImage.startsWith("data:image");
        const base64Image = isBase64 ? uploadedImage.split(",")[1] : (await convertBlobToBase64(uploadedImage)).split(",")[1];
  
        // Ajouter l'image en base64 dans image_file_b64
        formData.append('image_file_b64', base64Image);
  
        const response = await fetch('https://api.remove.bg/v1.0/removebg', {
          method: 'POST',
          // headers: { 'X-Api-Key': process.env.REACT_APP_REMOVE_BG_API_KEY },
          headers: { 'X-Api-Key': REMOVE_BG_API_KEY },
          body: formData,
        });
  
        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          const blob = new Blob([arrayBuffer], { type: 'image/png' });
  
          const base64Reader = new FileReader();
          base64Reader.onloadend = () => setProcessedImage(base64Reader.result);
          base64Reader.readAsDataURL(blob);
          setStep(3); // Passe à l'étape finale pour l'aperçu
        } else {
          throw new Error(`${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('Error removing background:', error);
      if (error.message?.includes('402')) {
        //TODO 2 : vous avez dépassé votre abonnement mensuel (...) vous pouvez remove.bg/fr/upload (cf phrase thibaut)
        setErrorApiCall({ message: "Quota d'appels API dépassé. Veuillez contacter votre administrateur pour configurer votre clef API." });
      } else  {
        setErrorApiCall(true);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction utilitaire pour convertir Blob en Base64
  const convertBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Crop handling
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropImage = async () => {
    try {
      const croppedImage = await getCroppedImg(uploadedImage, croppedAreaPixels);
      setProcessedImage(croppedImage);
      //setPreviewImage(croppedImage);
      setShowCropper(false);
      setStep(3);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  // Save the final chosen image and reset to step 1
  const handleSaveImageChoice = async (image) => {
    setPreviewImage(image);
    setUploadedImage(image);
    setProcessedImage(null);
    setStep(2);

  // Fonction pour convertir base64/Blob en File
  const convertToImageFile = async (imageData, fileName = 'image.jpg') => {
    if (typeof imageData === 'string' && imageData.startsWith('data:image/')) {
      // Base64 -> File
      const response = await fetch(imageData);
      const blob = await response.blob();
      return new File([blob], fileName, { type: blob.type });
    } else if (imageData instanceof Blob) {
      // Blob -> File
      return new File([imageData], fileName, { type: imageData.type });
    }
    return null; // Si ce n'est ni un Blob ni un base64, retourner null
  };

  const imageFile = await convertToImageFile(image);

  if (imageFile) {
    // Appeler onImageSave avec le fichier
    onImageSave(imageFile);
  } else {
    console.error('L’image n’est ni un Blob ni un Base64.');
  }
  };

    return (
    <Box>
        <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
        {/* Preview 1: Image in player data */}
        <Box>
          {isEditable && displayImageLabel && <Box className='small-padding'><Typography variant="subtitle1" sx={{color: '#6D6E73'}}>Image actuelle</Typography></Box>}
          {previewImage && <Box sx={{ width: 200, height: 200, border: '2px solid #ccc', borderRadius: '8px', overflow: 'hidden' }}>
              <CardMedia component="img" image={previewImage} alt="Image actuelle" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>}
            {!previewImage && 
            <Box className='default-image'>
              <FontAwesomeIcon icon={type ==='avatar' ? faUser : faImage} />
            </Box>
            }
        </Box>

        {/* Preview 2: Display processed image if available */}
        {processedImage && (
          <Box>
            {displayImageLabel && <Typography variant="subtitle1" sx={{color: '#6D6E73'}}>Image modifiée</Typography>}
            <Box sx={{ width: 200, height: 200, border: '2px solid #ccc', borderRadius: '8px', overflow: 'hidden' }}>
              <CardMedia component="img" image={processedImage} alt="Image modifiée" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
          </Box>
        )}
      </Box>
      {isEditable && step !== 1 &&  (
        <Button onClick={handleReset} variant="contained" component="label" sx={{ mt: 2 }}>
          Réinitialiser
        </Button>
      )}

      {/* Step 1: Upload image button */}
      {isEditable && step === 1 && (
        <Box className='centered-medium-wrapper'>
                 <DsasButton 
                  icon={faImage}
                  label={uploadButtonLabel}
                  variant="secondary" 
                  upload={true} 
                  onChange={handleSelectImage}
                />
         </Box>
      )}

      {/* Step 2: Editing tools */}
      { isEditable && step === 2 && (
        <>

        <Box className='centered-medium-wrapper'>

          <DsasButton 
            label="Recadrer" 
            icon={faCrop}
            variant="secondary" 
            onClick={() => setShowCropper(true)}
          />

          <DsasButton 
            label="Arrière-plan" 
            icon={faTrash}
            variant="secondary" 
            disabled={loading && canCallAPI}
            onClick={handleRemoveBackground}
          />

        </Box>

        <Box className='centered-wrapper'>

          {errorApiCall && (
            <Alert
                severity="warning"
                sx={{ mt: 2, maxWidth: 720, textAlign: 'left' }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Vos crédits mensuels sont actuellement épuisés. Ils seront de nouveau disponibles à partir du 26 du mois.
                </Typography>

                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  En attendant, vous pouvez supprimer gratuitement l’arrière-plan de votre image à l’aide de l’un des deux outils suivants :
                </Typography>

                <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 3 }}>
                  <li>
                    <Link
                      href="https://www.canva.com/features/background-remover/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Canva
                    </Link>{' '}
                    — nécessite la création d’un compte.
                  </li>
                  <li>
                    <Link
                      href="https://pixlr.com/fr/remove-background/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Pixlr
                    </Link>{' '}
                    — ne nécessite pas de création de compte.
                  </li>
                </Box>

                <Typography variant="body2">
                  Une fois votre image détourée, vous pourrez l’importer directement dans notre solution.
                </Typography>
              </Alert>
            // <Typography variant="body2" color="red">
            //   {errorApiCall.message || 'Erreur lors de l’appel à l’API de suppression de l’arrière-plan. Veuillez réessayer plus tard.'}
            // </Typography>
          )}

        </Box>
        </>
      )}

      {/* Cropper modal */}
      <Modal open={showCropper} onClose={() => setShowCropper(false)}>
        <Box sx={{ display: 'grid'}}>
          <Box style={{backgroundColor: 'red'}}>
            <Cropper
              image={uploadedImage}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: 'white'
          }}>
            <Box sx={{display: 'inline-flex',
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    flexDirection: 'row'
            }}>

              <ToggleButtonGroup
                  value={aspect}
                  exclusive
                  onChange={(e, newAspect) => {
                    if (!newAspect) return;       // éviter de remettre aspect à null
                    setAspect(newAspect);

                    // optionnel : recentrer / reset zoom à chaque changement de ratio
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                  }}
                  aria-label="Aspect ratio"
                  sx={{ m: 2, backgroundColor: 'rgb(255 255 255)' }}
                >
                  {RATIO_PRESETS.map((ratio) => (
                    <ToggleButton
                      key={ratio.label}
                      value={ratio.value}
                      aria-label={ratio.label}
                      sx={{ backgroundColor: 'rgb(255 255 255 / 82%)' }}
                    >
                      {ratio.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
          </Box> 
          {/* Slider de zoom */}
          <Box sx={{ px: 2, 
            minWidth: '150px', backgroundColor: 'rgb(255 255 255)', zIndex: 5, borderRadius: '6px', margin: '5px' }}>
            <Typography variant="body2" sx={{ mb: 1, color:'#47464B',
                  textAlign: 'center',
                  paddingTop: '5px',
                  margin: 0 }}>
              Zoom
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={4}
              step={0.1}
              onChange={(_, value) => setZoom(value)}
            />
          </Box>

          {/* Bouton appliquer */}
          <Box sx={{ backgroundColor: 'rgb(255 255 255)', margin: '10px' }}>
            <Button sx={{ backgroundColor: 'rgb(255 255 255 / 82%)', padding: '12px' }}  onClick={handleCropImage}>
              Appliquer
            </Button>
          </Box>
          </Box>
        </Box>
      </Modal>

      {/* Step 3: Choose final image */}
      {step === 3 && (
        <Box className='centered-medium-wrapper'>

          <DsasButton 
            label="Garder l'image originale" 
            variant="secondary" 
            onClick={() => handleSaveImageChoice(uploadedImage)}
          />

          <DsasButton 
            label="Garder l'image modifiée" 
            variant="secondary" 
            onClick={() => handleSaveImageChoice(processedImage)}
          />

        </Box>
        // <Box sx={{ mt: 2 }}>
        //   <Button variant="contained" onClick={() => handleSaveImageChoice(uploadedImage)}>
        //     Garder l'image originale
        //   </Button>
        //   <Button variant="contained" color="primary" onClick={() => handleSaveImageChoice(processedImage)} sx={{ ml: 2 }}>
        //     Garder l'image modifiée
        //   </Button>
        // </Box>
      )}
    </Box>
    )
};

export default ImageHandlerTool;